import { useEffect, useRef, useState } from "react";
import {
  getAnonId,
  getHandle,
  hasHandle,
  HandleModal,
  haptic,
  Leaderboard,
  ShareSheet,
  track,
  trackPageView,
  useLeaderboard,
} from "@karrtopelka/game-kit";
import type { CardProps } from "@karrtopelka/cards/types";

import { Board } from "./components/Board.js";
import { buildPlinkoCard } from "./lib/buildCard.js";
import { loadCardAssets } from "./lib/cardAssets.js";
import { usePlinkoStore, type DropResult } from "./store.js";

/** Multiplier worth a card (and the share nudge). */
const SHARE_AT = 2;

/** The one shared leaderboard hook instance (desktop rail + mobile board). */
type LeaderboardHook = ReturnType<typeof useLeaderboard>;

export function App() {
  const phase = usePlinkoStore((s) => s.phase);

  const warmed = useRef(false);
  useEffect(() => {
    if (!warmed.current && phase !== "idle") {
      warmed.current = true;
      void loadCardAssets().catch(() => {});
    }
  }, [phase]);

  useEffect(() => trackPageView("plinko"), []);
  const started = useRef(false);
  useEffect(() => {
    if (phase === "dropping" && !started.current) {
      started.current = true;
      track("plinko", "play_start");
    }
    if (phase === "done") track("plinko", "play_done");
  }, [phase]);

  // One leaderboard instance shared by the desktop rail and the mobile board.
  const board = useLeaderboard("plinko");
  const [handle, setHandle] = useState<string | null>(() => getHandle());

  return (
    <main
      className="flex min-h-screen min-h-[100dvh] flex-col items-center gap-5 bg-bg px-6 text-text lg:grid lg:grid-cols-[minmax(200px,260px)_minmax(0,32rem)_minmax(240px,300px)] lg:content-start lg:items-start lg:justify-center lg:gap-x-10 lg:gap-y-8 lg:px-10"
      style={{
        paddingTop: "calc(2.5rem + env(safe-area-inset-top))",
        paddingBottom: "calc(2.5rem + env(safe-area-inset-bottom))",
      }}
    >
      <header className="flex animate-fade-up flex-col items-center lg:col-span-3">
        <h1 className="text-3xl font-extrabold tracking-tight lg:text-5xl">
          plinko<span className="animate-glow text-accent">.</span>
        </h1>
        <p className="text-sm text-text-muted lg:text-base">
          one ball. 12 rows. 175x on the edges.
        </p>
      </header>

      <LeftRail />

      <Game board={board} handle={handle} onHandle={setHandle} />

      <aside
        className="sticky top-8 hidden animate-fade-up flex-col gap-3 lg:flex"
        style={{ animationDelay: "240ms" }}
      >
        <div className="rounded-lg border border-border bg-surface/60 p-4 backdrop-blur">
          <h2 className="mb-3 text-center text-xs font-semibold uppercase tracking-wide text-text-muted">
            luckiest buckets
          </h2>
          <Leaderboard
            entries={board.entries}
            loading={board.loading}
            myHandle={handle}
            formatValue={(v) => `${(v / 100).toFixed(2)}x`}
            emptyLabel="no big buckets hit yet — drop one."
            range={board.range}
            onRange={board.setRange}
          />
        </div>
      </aside>

      <footer
        className="mt-2 flex animate-fade-up flex-col items-center gap-1 text-xs text-text-faint lg:col-span-3 lg:mt-4"
        style={{ animationDelay: "320ms" }}
      >
        <div className="flex flex-col items-center gap-1 lg:hidden">
          <a
            href="https://limbo.karrtopelka.com"
            className="hover:text-accent hover:underline"
          >
            also try limbo — call your multiplier →
          </a>
          <a href="https://karrtopelka.com" className="hover:text-text-muted">
            karrtopelka.com
          </a>
        </div>
        <div className="hidden items-center gap-3 lg:flex">
          <span className="text-[10px] uppercase tracking-widest text-text-faint">
            more games
          </span>
          <GameChip href="https://limbo.karrtopelka.com" name="limbo" hook="call your multiplier" />
          <GameChip href="https://mines.karrtopelka.com" name="mines" hook="dodge the bombs" />
          <GameChip href="https://crash.karrtopelka.com" name="crash" hook="cash out in time" />
          <a
            href="https://karrtopelka.com"
            className="text-text-faint transition-colors duration-200 hover:text-accent"
          >
            karrtopelka.com →
          </a>
        </div>
      </footer>
    </main>
  );
}

function GameChip({ href, name, hook }: { href: string; name: string; hook: string }) {
  return (
    <a
      href={href}
      className="flex flex-col rounded-lg border border-border bg-surface/60 px-4 py-2 backdrop-blur transition-all duration-200 hover:-translate-y-0.5 hover:border-accent hover:shadow-glow"
    >
      <span className="text-sm font-semibold text-text">{name}</span>
      <span className="text-[11px] text-text-faint">{hook}</span>
    </a>
  );
}

/** Desktop-only left rail: how it works + session pit display. */
function LeftRail() {
  const history = usePlinkoStore((s) => s.history);
  const best = usePlinkoStore((s) => s.best);
  const avg =
    history.length > 0
      ? history.reduce((s, h) => s + h.mult, 0) / history.length
      : 0;

  return (
    <aside
      className="sticky top-8 hidden animate-fade-up flex-col gap-4 lg:flex"
      style={{ animationDelay: "160ms" }}
    >
      <div className="rounded-lg border border-border bg-surface/60 p-4 backdrop-blur">
        <h2 className="mb-2 text-xs font-semibold uppercase tracking-wide text-text-muted">
          how it works
        </h2>
        <ul className="flex flex-col gap-1.5 text-sm text-text-muted">
          <li>one ball, 12 rows of pegs.</li>
          <li>edges pay 175x. center pays dust.</li>
          <li>2x+ mints a share card.</li>
        </ul>
        <p className="mt-3 text-xs text-text-faint">
          <kbd className="rounded border border-border bg-surface-raised px-1.5 py-0.5 font-mono text-[10px] text-text">
            space
          </kbd>{" "}
          to drop
        </p>
      </div>

      <div className="rounded-lg border border-border bg-surface/60 p-4 backdrop-blur">
        <h2 className="mb-2 text-xs font-semibold uppercase tracking-wide text-text-muted">
          your table
        </h2>
        <div className="flex flex-col gap-2">
          <div className="flex items-baseline justify-between">
            <span className="text-xs text-text-faint">best bucket</span>
            <span
              className={`font-display text-xl font-bold ${
                best >= 1000 ? "text-gold" : best >= 200 ? "text-win" : "text-text"
              }`}
            >
              {best > 0 ? `${(best / 100).toFixed(2)}x` : "—"}
            </span>
          </div>
          <div className="flex items-baseline justify-between">
            <span className="text-xs text-text-faint">
              avg of last {history.length || 0}
            </span>
            <span className="font-display text-xl font-bold text-text-muted">
              {history.length > 0 ? `${avg.toFixed(2)}x` : "—"}
            </span>
          </div>
        </div>
      </div>
    </aside>
  );
}

function Game({
  board,
  handle,
  onHandle,
}: {
  board: LeaderboardHook;
  handle: string | null;
  onHandle: (h: string) => void;
}) {
  const phase = usePlinkoStore((s) => s.phase);
  const result = usePlinkoStore((s) => s.result);
  const history = usePlinkoStore((s) => s.history);
  const best = usePlinkoStore((s) => s.best);
  const startDrop = usePlinkoStore((s) => s.startDrop);
  const land = usePlinkoStore((s) => s.land);
  const reset = usePlinkoStore((s) => s.reset);

  const [showHandle, setShowHandle] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [card, setCard] = useState<CardProps | null>(null);
  const [pending, setPending] = useState<DropResult | null>(null);
  const [dropId, setDropId] = useState(0);

  const doDrop = async () => {
    setError(null);
    setCard(null);
    setPending(null);
    try {
      const res = await fetch("/api/plinko/drop", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ anonId: getAnonId(), handle: getHandle() }),
      });
      if (!res.ok) {
        setError(res.status === 429 ? "easy — one ball per 2s" : "drop failed");
        return;
      }
      const drop = (await res.json()) as DropResult;
      setPending(drop);
      setDropId((n) => n + 1);
      haptic.tap();
      startDrop();
    } catch {
      setError("network hiccup — try again");
    }
  };

  const onDrop = () => {
    if (phase === "dropping") return;
    if (hasHandle()) void doDrop();
    else setShowHandle(true);
  };

  // Spacebar = feed the cabinet another ball (desktop). Skips form fields, real
  // buttons (native space-click would double-fire), and the handle modal.
  const dropRef = useRef<() => void>(() => {});
  dropRef.current = () => {
    if (showHandle) return;
    onDrop();
  };
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.code !== "Space" || e.repeat) return;
      const t = e.target;
      if (
        t instanceof HTMLElement &&
        (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.tagName === "BUTTON")
      )
        return;
      e.preventDefault();
      dropRef.current();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const onLanded = () => {
    if (!pending) return;
    if (pending.mult >= SHARE_AT) haptic.newBest();
    else if (pending.mult < 1) haptic.tooSoon();
    else haptic.land();
    land(pending);
    if (pending.mult >= SHARE_AT && pending.value !== undefined) {
      setCard(
        buildPlinkoCard(pending.mult, pending.value, getHandle() ?? "you", {
          total: pending.total ?? 0,
          percentile: pending.percentile ?? 100,
        }),
      );
      void board.refresh();
    }
  };

  const won = result !== null && result.mult >= SHARE_AT;
  const dud = result !== null && result.mult < 1;

  return (
    <div
      className="flex w-full max-w-md animate-fade-up flex-col items-center gap-4 lg:max-w-none"
      style={{ animationDelay: "80ms" }}
    >
      {/* last drops */}
      {history.length > 0 && (
        <div className="flex flex-wrap items-center justify-center gap-1.5">
          {history.map((h, i) => (
            <span
              key={i}
              className={`rounded-full border px-2 py-0.5 font-mono text-[11px] transition-colors duration-200 hover:border-accent/60 ${
                h.mult >= 10
                  ? "border-gold/50 text-gold"
                  : h.mult >= 2
                    ? "border-win/40 text-win"
                    : h.mult >= 1
                      ? "border-border text-text-muted"
                      : "border-border text-text-faint"
              } ${i === 0 ? "animate-pop" : ""}`}
            >
              {h.mult}x
            </span>
          ))}
        </div>
      )}

      <div
        className={`lg:shadow-[0_0_60px_rgba(255,111,165,0.07)] ${
          phase === "done"
            ? won
              ? "w-full animate-flash-win rounded-lg"
              : dud
                ? "w-full animate-flash-bust rounded-lg"
                : "w-full rounded-lg"
            : "w-full rounded-lg"
        }`}
      >
        <Board
          path={phase === "idle" ? null : (pending?.path ?? null)}
          dropId={dropId}
          onLanded={onLanded}
        />
      </div>

      {/* verdict line */}
      {phase === "done" && result && (
        <section className="flex flex-col items-center">
          <span
            className={`font-display text-5xl font-extrabold lg:text-6xl ${
              result.mult >= 10
                ? "text-gold"
                : result.mult >= 2
                  ? "text-win"
                  : result.mult >= 1
                    ? "text-text"
                    : "text-text-faint"
            }`}
          >
            {result.mult}x
          </span>
          <span className="text-sm text-text-muted">
            {result.mult >= 10
              ? "the ball chose violence 🎰"
              : result.mult >= 2
                ? "decent bucket — card's ready"
                : result.mult >= 1
                  ? "break-even bounce"
                  : "center bucket. the dud zone."}
          </span>
          {result.total !== undefined && result.total >= 10 && result.mult >= 1 && (
            <p className="text-sm text-text">
              #{result.rank} of {result.total}
              <span className="text-accent"> · top {result.percentile}%</span>
            </p>
          )}
        </section>
      )}

      {error && <p className="text-sm text-accent">{error}</p>}

      <button
        type="button"
        onClick={onDrop}
        disabled={phase === "dropping"}
        className="w-full animate-neon-pulse rounded-md bg-accent px-4 py-4 text-lg font-bold text-bg transition-all duration-200 hover:-translate-y-0.5 hover:bg-[#ff9ec6] active:scale-[0.99] disabled:animate-none disabled:opacity-40 disabled:hover:translate-y-0"
      >
        {phase === "dropping" ? "bouncing…" : phase === "done" ? "drop another" : "DROP"}
      </button>
      {best > 0 && phase !== "dropping" && (
        <span className="text-xs text-text-faint lg:hidden">
          your best bucket: {(best / 100).toFixed(2)}x
        </span>
      )}

      {phase === "done" && card && (
        <ShareSheet
          card={card}
          replyLink="plinko.karrtopelka.com/r"
          caption={`${result?.mult}x off one plinko ball. ${card.provocation}`}
          loadAssets={loadCardAssets}
        />
      )}

      {phase === "done" && !card && (
        <button
          type="button"
          onClick={reset}
          className="text-sm text-text-muted transition-colors duration-200 hover:text-text"
        >
          clear
        </button>
      )}

      {/* below-the-board leaderboard on mobile; the desktop rail owns it on lg+ */}
      <section className="flex w-full flex-col items-center gap-3 border-t border-border pt-6 lg:hidden">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-text-muted">
          luckiest buckets
        </h2>
        <Leaderboard
          entries={board.entries}
          loading={board.loading}
          myHandle={handle}
          formatValue={(v) => `${(v / 100).toFixed(2)}x`}
          emptyLabel="no big buckets hit yet — drop one."
          range={board.range}
          onRange={board.setRange}
        />
      </section>

      {showHandle && (
        <HandleModal
          onCancel={() => setShowHandle(false)}
          onDone={(h) => {
            setShowHandle(false);
            onHandle(h);
            void doDrop();
          }}
        />
      )}
    </div>
  );
}
